// components/AnalysisChart.tsx
import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { ServiceData, TimeGrouping } from '../types/analysis.types';
import { SERVICE_COLORS } from '../utils/constants';
import { formatDateLabel } from '../utils/dateUtils';
import { useIsSmallScreen } from '../../../hooks/useIsSmallScreen';

interface AnalysisChartProps {
    isLoading: boolean;
    serviceData: ServiceData[];
    timeGrouping: TimeGrouping;
    getCompanyName: (key: string) => string;
    getSelectedGroupName: () => string;
    selectedUserGroup: string | null;
    /** Replaces the default empty text, e.g. when no picked building matches the filters */
    emptyMessage?: string | null;
}

const AnalysisChart: React.FC<AnalysisChartProps> = ({
                                                         isLoading,
                                                         serviceData,
                                                         timeGrouping,
                                                         getCompanyName,
                                                         getSelectedGroupName,
                                                         selectedUserGroup,
                                                         emptyMessage
                                                     }) => {
    // Phones get slimmer chart margins; the rotated Y-axis label doesn't fit
    // there, so it becomes a caption above the chart.
    const isSmallScreen = useIsSmallScreen();

    const getUniqueCompanyKeys = (): string[] => {
        const companyKeys = new Set<string>();
        serviceData.forEach(item => {
            Object.keys(item).forEach(key => {
                if (key !== 'date' && key !== 'count' && typeof item[key] === 'number' && item[key] > 0) {
                    companyKeys.add(key);
                }
            });
        });
        return Array.from(companyKeys);
    };

    if (isLoading) {
        return (
            <div className="loading-container">
                <p>Laddar data...</p>
            </div>
        );
    }

    if (emptyMessage) {
        return (
            <div className="empty-container">
                <p>{emptyMessage}</p>
            </div>
        );
    }

    if (serviceData.length === 0) {
        return (
            <div className="empty-container">
                <p>
                    Ingen data tillgänglig inom valt datumintervall
                    {` för ${getSelectedGroupName()}`}
                    och valda byggnadstyper. Varje byggnad räknas en gång per dag baserat på senaste besöket från respektive företag.
                </p>
            </div>
        );
    }

    const companyKeys = getUniqueCompanyKeys();

    return (
        <>
        {isSmallScreen && <p className="analysis-chart-caption">Byggnadsbesök</p>}
        <div className="chart-container">
            <ResponsiveContainer width="100%" height="100%">
                <BarChart
                    data={serviceData}
                    margin={isSmallScreen ? { top: 10, right: 8, left: 0, bottom: 40 } : { top: 20, right: 30, left: 30, bottom: 60 }}
                >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                        dataKey="date"
                        angle={-45}
                        textAnchor="end"
                        height={isSmallScreen ? 50 : 60}
                        tickMargin={isSmallScreen ? 12 : 20}
                        tickFormatter={(value) => formatDateLabel(value, timeGrouping)}
                    />
                    <YAxis
                        // An explicit undefined width overrides recharts' default (60) under React 19 and nothing is drawn
                        width={isSmallScreen ? 32 : 60}
                        label={isSmallScreen ? undefined : { value: 'Byggnadsbesök', angle: -90, position: 'insideLeft', offset: -15 }}
                    />
                    <Tooltip
                        formatter={(value: number, name: string) => {
                            const companyName = getCompanyName(name);
                            return [`${value} besök`, companyName];
                        }}
                        labelFormatter={(label: string) => {
                            const periodLabel = timeGrouping === 'daily' ? 'Datum' :
                                timeGrouping === 'weekly' ? 'Veckor' :
                                    timeGrouping === 'monthly' ? 'Månader' : 'År';
                            return `${periodLabel}: ${formatDateLabel(label, timeGrouping)}`;
                        }}
                    />
                    <Legend
                        formatter={(value: string) => getCompanyName(value)}
                        wrapperStyle={{ paddingTop: '20px' }}
                    />
                    {companyKeys.map((companyKey, index) => (
                        <Bar
                            key={companyKey}
                            dataKey={companyKey}
                            name={companyKey}
                            stackId="visits"
                            fill={SERVICE_COLORS[index % SERVICE_COLORS.length]}
                            radius={index === companyKeys.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                        />
                    ))}
                </BarChart>
            </ResponsiveContainer>
        </div>
        </>
    );
};

export default AnalysisChart;